import { useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { verifyMfaOtp, verifyUser } from "../Services/Home";
import { AUTH_COOKIE_KEY, AUTH_STORAGE_KEY } from "../Services/tokenAuth";

const getNestedData = (payload) => payload?.data ?? payload ?? {};
const getUserDetails = (payload) => getNestedData(payload)?.userDetails ?? {};
const getCallbackUrl = () => `${window.location.origin}/WhatsApp/Chat`;
const getTokenFromCallbackUrl = (callbackUrl) => {
	if (!callbackUrl) return "";

	try {
		const parsedUrl = new URL(callbackUrl);
		return parsedUrl.searchParams.get("token") || "";
	} catch {
		return "";
	}
};

const pickValue = (source, keys) =>
	keys.find((key) => source?.[key] !== undefined && source?.[key] !== null)
		? source[
				keys.find(
					(key) => source?.[key] !== undefined && source?.[key] !== null,
				)
			]
		: undefined;

const hasTruthyFlag = (value) => {
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value > 0;
	if (typeof value === "string") {
		return ["true", "success", "ok", "verified"].includes(value.toLowerCase());
	}

	return false;
};

const isOtpRequired = (payload) => {
	const data = getNestedData(payload);
	const userDetails = getUserDetails(payload);
	const explicitFlag = pickValue(data, [
		"requiresMfa",
		"requireMfa",
		"mfaRequired",
		"isMfaRequired",
		"requiresOtp",
		"otpRequired",
	]);

	if (hasTruthyFlag(explicitFlag)) {
		return true;
	}

	if (hasTruthyFlag(userDetails?.mfaEnabled)) {
		return true;
	}

	return Boolean(
		pickValue(data, [
			"qrCode",
			"mfaToken",
			"mfaSession",
			"mfaReference",
			"sessionId",
			"challengeId",
			"requestId",
		]),
	);
};

const isAuthenticated = (payload) => {
	const data = getNestedData(payload);
	const userDetails = getUserDetails(payload);
	const callbackToken = getTokenFromCallbackUrl(
		pickValue(data, ["callbackURL", "callbackUrl", "returnUrl"]),
	);

	return (
		Boolean(
			pickValue(userDetails, [
				"token",
				"accessToken",
				"jwtToken",
				"jwt",
				"authToken",
			]) ||
			callbackToken ||
			pickValue(data, ["token", "accessToken", "jwtToken", "jwt", "authToken"]),
		) || hasTruthyFlag(pickValue(data, ["isAuthenticated", "authenticated"]))
	);
};

const buildLoginPayload = ({ username, password }) => ({
	username,
	userName: username,
	email: username,
	password,
});

const buildOtpPayload = ({ username, otp, challenge }) => ({
	username,
	userName: username,
	otp,
	mfaOtp: otp,
	code: otp,
	callbackUrl: getCallbackUrl(),
	callBackUrl: getCallbackUrl(),
	returnUrl: getCallbackUrl(),
	appUid: challenge?.userDetails?.appUid,
	userUID: challenge?.userDetails?.userUID,
	userUid: challenge?.userDetails?.userUID,
	mfaSecret: challenge?.userDetails?.mfaSecret,
	sessionId: challenge?.sessionId,
	mfaSession: challenge?.mfaSession,
	mfaToken: challenge?.mfaToken,
	challengeId: challenge?.challengeId,
	requestId: challenge?.requestId,
});

const getErrorMessage = (error, fallbackMessage) => {
	const responseData = error?.response?.data;

	if (typeof responseData === "string" && responseData.trim()) {
		return responseData;
	}

	return (
		responseData?.message ||
		responseData?.error ||
		responseData?.title ||
		error?.message ||
		fallbackMessage
	);
};

export default function Home() {
	const navigate = useNavigate();

	const [credentials, setCredentials] = useState({
		username: "",
		password: "",
	});
	const [otp, setOtp] = useState("");
	const [challenge, setChallenge] = useState(null);
	const [step, setStep] = useState("login");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [info, setInfo] = useState("");
	const challengeDetails = getUserDetails(challenge);

	const headerCopy = useMemo(
		() =>
			step === "login"
				? {
						title: "Sign in to VRS Samadhan Sahayak",
						subtitle:
							"Use your registered account to access chats, templates, and WhatsApp operations.",
					}
				: {
						title: "Verify your sign in",
						subtitle:
							"Enter the OTP sent for multi-factor authentication to finish signing in securely.",
					},
		[step],
	);

	const completeAuth = (payload) => {
		const authData = getNestedData(payload);
		const callbackUrl = pickValue(authData, [
			"callbackURL",
			"callbackUrl",
			"returnUrl",
		]);
		const token =
			pickValue(getUserDetails(payload), [
				"token",
				"accessToken",
				"jwtToken",
				"jwt",
				"authToken",
			]) ||
			pickValue(authData, [
				"token",
				"accessToken",
				"jwtToken",
				"jwt",
				"authToken",
			]) ||
			getTokenFromCallbackUrl(callbackUrl);

		if (token) {
			Cookies.set(AUTH_COOKIE_KEY, token, { sameSite: "lax" });
		}

		localStorage.setItem(
			AUTH_STORAGE_KEY,
			JSON.stringify({
				username: credentials.username,
				token,
				userDetails: getUserDetails(payload),
				...authData,
			}),
		);
		navigate("/WhatsApp/Chat");
	};

	const handleLogin = async (event) => {
		event.preventDefault();
		setLoading(true);
		setError("");
		setInfo("");

		try {
			const response = await verifyUser(buildLoginPayload(credentials));
			const payload = getNestedData(response.data);

			if (isOtpRequired(payload)) {
				setChallenge(payload);
				setStep("otp");
				setInfo(
					payload?.message ||
						"Multi-factor authentication is enabled for this account. Scan the QR code if needed, then enter the OTP to continue.",
				);
				return;
			}

			if (isAuthenticated(payload)) {
				completeAuth(payload);
				return;
			}

			setError(
				payload?.message ||
					"The server did not return a usable authentication response.",
			);
		} catch (requestError) {
			setError(
				getErrorMessage(requestError, "Unable to verify your credentials."),
			);
		} finally {
			setLoading(false);
		}
	};

	const handleOtpSubmit = async (event) => {
		event.preventDefault();
		setLoading(true);
		setError("");
		setInfo("");

		try {
			const response = await verifyMfaOtp(
				buildOtpPayload({
					username: credentials.username,
					otp,
					challenge,
				}),
			);
			const payload = getNestedData(response.data);

			if (isAuthenticated(payload)) {
				completeAuth(payload);
				return;
			}

			setError(
				payload?.message ||
					"OTP verification did not return a usable authentication response.",
			);
		} catch (requestError) {
			setError(getErrorMessage(requestError, "Unable to verify the OTP."));
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		setCredentials((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const goBackToLogin = () => {
		setStep("login");
		setOtp("");
		setChallenge(null);
		setError("");
		setInfo("");
	};

	return (
		<div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f7f0dc,_transparent_32%),linear-gradient(135deg,_#efe6cf_0%,_#fbfaf5_45%,_#d7ebe6_100%)] px-4 py-8 sm:px-6 lg:px-8">
			<div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl overflow-hidden rounded-[32px] border border-white/70 bg-white/80 shadow-[0_30px_80px_rgba(88,66,34,0.14)] backdrop-blur">
				<div className="hidden w-[46%] flex-col justify-between bg-[#133b34] p-10 text-white lg:flex">
					<div>
						<div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-[#d8efe9]">
							VRS WhatsApp Suite
						</div>
						<h1 className="mt-8 max-w-sm text-5xl font-semibold leading-tight">
							Conversations, templates, and customer response in one workspace.
						</h1>
						<p className="mt-6 max-w-md text-sm leading-7 text-[#c4ddd7]">
							Authenticate with your organization account to manage live
							WhatsApp chats, review message templates, and keep every
							interaction moving.
						</p>
					</div>

					<div className="grid gap-4">
						<div className="rounded-3xl border border-white/10 bg-white/10 p-5">
							<p className="text-sm font-medium text-[#eef8f5]">
								Secure access
							</p>
							<p className="mt-2 text-sm leading-6 text-[#c4ddd7]">
								Your sign-in runs against the configured CLS authentication API
								with app-key based authorization.
							</p>
						</div>
						<div className="rounded-3xl border border-white/10 bg-black/10 p-5">
							<p className="text-sm font-medium text-[#eef8f5]">MFA ready</p>
							<p className="mt-2 text-sm leading-6 text-[#c4ddd7]">
								If your account requires OTP verification, the page switches
								seamlessly into the second step without leaving the screen.
							</p>
						</div>
					</div>
				</div>

				<div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
					<div className="w-full max-w-md">
						<div className="mb-8">
							<p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#977748]">
								Authentication
							</p>
							<h2 className="mt-3 text-3xl font-semibold text-[#1f2a27]">
								{headerCopy.title}
							</h2>
							<p className="mt-3 text-sm leading-6 text-[#5f6f68]">
								{headerCopy.subtitle}
							</p>
						</div>

						{error ? (
							<div className="mb-4 rounded-2xl border border-[#efb8b8] bg-[#fff3f1] px-4 py-3 text-sm text-[#a23a3a]">
								{error}
							</div>
						) : null}

						{info ? (
							<div className="mb-4 rounded-2xl border border-[#c4dccc] bg-[#f3fbf5] px-4 py-3 text-sm text-[#23553f]">
								{info}
							</div>
						) : null}

						{step === "login" ? (
							<form onSubmit={handleLogin} className="space-y-5">
								<div>
									<label
										htmlFor="username"
										className="mb-2 block text-sm font-medium text-[#30413d]"
									>
										Username or email
									</label>
									<input
										id="username"
										name="username"
										type="text"
										value={credentials.username}
										onChange={handleChange}
										autoComplete="username"
										required
										placeholder="Enter your username"
										className="w-full rounded-2xl border border-[#d6ddd3] bg-white px-4 py-3 text-[#1f2a27] shadow-sm outline-none transition focus:border-[#245f54] focus:ring-4 focus:ring-[#245f54]/10"
									/>
								</div>

								<div>
									<label
										htmlFor="password"
										className="mb-2 block text-sm font-medium text-[#30413d]"
									>
										Password
									</label>
									<input
										id="password"
										name="password"
										type="password"
										value={credentials.password}
										onChange={handleChange}
										autoComplete="current-password"
										required
										placeholder="Enter your password"
										className="w-full rounded-2xl border border-[#d6ddd3] bg-white px-4 py-3 text-[#1f2a27] shadow-sm outline-none transition focus:border-[#245f54] focus:ring-4 focus:ring-[#245f54]/10"
									/>
								</div>

								<button
									type="submit"
									disabled={
										loading ||
										!credentials.username.trim() ||
										!credentials.password.trim()
									}
									className="w-full rounded-2xl bg-[#133b34] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#0d2d27] disabled:cursor-not-allowed disabled:opacity-60"
								>
									{loading ? "Signing in..." : "Continue"}
								</button>
							</form>
						) : (
							<form onSubmit={handleOtpSubmit} className="space-y-5">
								{challengeDetails?.name ? (
									<div className="rounded-3xl border border-[#d6ddd3] bg-[#f8faf7] p-4">
										<p className="text-sm font-semibold text-[#1f2a27]">
											{challengeDetails.name}
										</p>
									</div>
								) : null}

								<div>
									<label
										htmlFor="otp"
										className="mb-2 block text-sm font-medium text-[#30413d]"
									>
										One-time password
									</label>
									<input
										id="otp"
										name="otp"
										type="text"
										inputMode="numeric"
										value={otp}
										onChange={(event) => setOtp(event.target.value)}
										required
										placeholder="Enter OTP"
										className="w-full rounded-2xl border border-[#d6ddd3] bg-white px-4 py-3 text-[#1f2a27] shadow-sm outline-none transition focus:border-[#245f54] focus:ring-4 focus:ring-[#245f54]/10"
									/>
								</div>

								<button
									type="submit"
									disabled={loading || !otp.trim()}
									className="w-full rounded-2xl bg-[#133b34] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#0d2d27] disabled:cursor-not-allowed disabled:opacity-60"
								>
									{loading ? "Verifying..." : "Verify OTP"}
								</button>

								<button
									type="button"
									onClick={goBackToLogin}
									className="w-full rounded-2xl border border-[#d6ddd3] px-4 py-3 text-sm font-medium text-[#30413d] transition hover:bg-[#f6f7f4]"
								>
									Back to sign in
								</button>
							</form>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
